import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Collapse } from 'react-bootstrap';
import { getSettings, updateSettings } from '../services/api';
import { ThemeContext } from '../contexts/ThemeContext';
import { saveSessionTimeoutToStorage, getSessionTimeoutFromStorage } from '../utils/sessionManager';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger('settingsPage', 'User settings management page', false, 'DEBUG');

function Settings() {
  const [settings, setSettings] = useState({});
  const [sessionTimeout, setSessionTimeout] = useState(getSessionTimeoutFromStorage() / (60 * 1000)); // Convert from ms to minutes
  const [cardsExpanded, setCardsExpanded] = useState({
    session: false,
    investment: false,
    display: false,
    weather: false,
    weatherTheme: false
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [originalSettings, setOriginalSettings] = useState(null);
  const [originalSessionTimeout, setOriginalSessionTimeout] = useState(null);
  const [originalWeatherCity, setOriginalWeatherCity] = useState(null);
  const [originalWeatherUnit, setOriginalWeatherUnit] = useState(null);
  const {
    theme, changeTheme, THEME_LIGHT, THEME_DARK, THEME_AUTO,
    weatherThemeEnabled, toggleWeatherTheme,
    weatherThemeMode, updateWeatherThemeMode,
    selectedWeatherTheme, updateSelectedWeatherTheme
  } = useContext(ThemeContext);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.data) {
        setSettings(response.data);
        setOriginalSettings({ ...response.data });
      }
      // Prefer the backend-synced value (so a timeout saved on another
      // device/session takes effect here too) and mirror it into
      // localStorage -- App.js's actual inactivity-timeout enforcement
      // reads getSessionTimeoutFromStorage() directly, not this page's
      // state, so the synced value has to land there to have any effect.
      // Falls back to whatever's already in localStorage (or the sessionManager
      // default) for a user who has never saved a session timeout before.
      const syncedSessionTimeoutMinutes = response.data?.session_timeout_minutes;
      const currentSessionTimeout = typeof syncedSessionTimeoutMinutes === 'number'
        ? syncedSessionTimeoutMinutes
        : getSessionTimeoutFromStorage() / (60 * 1000);
      if (typeof syncedSessionTimeoutMinutes === 'number') {
        saveSessionTimeoutToStorage(syncedSessionTimeoutMinutes * 60 * 1000);
      }
      setSessionTimeout(currentSessionTimeout);
      setOriginalSessionTimeout(currentSessionTimeout);
      setOriginalWeatherCity(localStorage.getItem('weatherCity') || 'New York');
      setOriginalWeatherUnit(localStorage.getItem('weatherUnit') || 'imperial');
      setLoading(false);
    } catch (err) {
      logger.error('Error fetching settings:', err);
      setError('Failed to load settings');
      setLoading(false);
    }
  };

  const normalizeValueForComparison = (value) => {
    if (value === null || typeof value === 'undefined') {
      return '';
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      return JSON.stringify(value);
    } catch (err) {
      return String(value);
    }
  };

  const hasSectionChanges = (sectionKeys) => {
    if (!originalSettings) {
      return false;
    }
    return sectionKeys.some((key) => {
      const currentValue = normalizeValueForComparison(settings[key]);
      const originalValue = normalizeValueForComparison(originalSettings[key]);
      return currentValue !== originalValue;
    });
  };

  const hasSessionTimeoutChanged = () => {
    return originalSessionTimeout !== null && sessionTimeout !== originalSessionTimeout;
  };

  const hasWeatherSettingsChanged = () => {
    const currentCity = localStorage.getItem('weatherCity') || 'New York';
    const currentUnit = localStorage.getItem('weatherUnit') || 'imperial';
    return currentCity !== originalWeatherCity || currentUnit !== originalWeatherUnit;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: parseInt(value, 10)
    }));
  };

  const handleTimeoutChange = (e) => {
    const minutes = parseInt(e.target.value, 10);
    setSessionTimeout(minutes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Persist to the backend (unlike every other section's Save button,
      // this used to only write to localStorage, so the setting never
      // actually synced -- a fresh session on another device, or simply
      // clearing localStorage, would silently revert to the default).
      await updateSettings({ session_timeout_minutes: sessionTimeout });

      const timeoutMs = sessionTimeout * 60 * 1000; // Convert minutes to ms
      saveSessionTimeoutToStorage(timeoutMs);
      setOriginalSessionTimeout(sessionTimeout);

      // Simulate a small delay to show saving state
      setTimeout(() => {
        setSaving(false);
        setSaved(true);

        // Reset to normal state after 2 seconds
        setTimeout(() => {
          setSaved(false);
        }, 2000);
      }, 500);
    } catch (err) {
      logger.error('Error updating settings:', err);
      setError('Failed to update settings');
      setSaving(false);
    }
  };

  // Determine the button text/state
  const getButtonText = () => {
    if (saving) return 'Saving...';
    if (saved) return 'Saved!';
    return 'Save Settings';
  };

  // Calculate button variant
  const getButtonVariant = () => {
    if (saved) return 'success';
    return 'primary';
  };

  return (
    <Container>
      <h1 className="my-4">Settings</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        {/* Left Column */}
        <Col md={6}>
          {/* Session Settings */}
          <Card className="mb-4">
            <Card.Header 
              as="h5" 
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setCardsExpanded(prev => ({ ...prev, session: !prev.session }))}
            >
              <span>
                <i className="bi bi-gear-fill me-2"></i>
                Session Settings
              </span>
              <i className={`bi bi-chevron-${cardsExpanded.session ? 'down' : 'right'}`}></i>
            </Card.Header>
            <Collapse in={cardsExpanded.session}>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Auto Logout After Inactivity (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    name="sessionTimeout"
                    value={sessionTimeout}
                    onChange={handleTimeoutChange}
                    min="0"
                    max="240"
                  />
                  <Form.Text className="text-muted">
                    Time in minutes before automatically logging out due to inactivity. 
                    Set to 0 to disable auto-logout. Maximum 240 minutes (4 hours).
                  </Form.Text>
                </Form.Group>
                
                <div className="d-flex justify-content-end">
                  <Button
                    variant={getButtonVariant()}
                    type="submit"
                    disabled={loading || saving || !hasSessionTimeoutChanged()}
                    className="position-relative"
                    style={{ transition: 'all 0.3s ease' }}
                  >
                    {getButtonText()}
                    {saved && (
                      <i className="bi bi-check-circle-fill position-absolute"
                         style={{
                           right: '10px',
                           top: '50%',
                           transform: 'translateY(-50%)'
                         }}
                      ></i>
                    )}
                  </Button>
                </div>
              </Form>
              </Card.Body>
            </Collapse>
          </Card>

          {/* Display Settings */}
          <Card className="mb-4">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setCardsExpanded(prev => ({ ...prev, display: !prev.display }))}
            >
              <span>
                <i className="bi bi-display me-2"></i>
                Display Settings
              </span>
              <i className={`bi bi-chevron-${cardsExpanded.display ? 'down' : 'right'}`}></i>
            </Card.Header>
            <Collapse in={cardsExpanded.display}>
              <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Display Theme</Form.Label>
                <div className="d-flex flex-column">
                  <Form.Check
                    type="radio"
                    id="theme-light"
                    name="theme"
                    label={
                      <div className="d-flex align-items-center">
                        <i className="bi bi-sun-fill me-2"></i>
                        <span>Light Mode</span>
                      </div>
                    }
                    checked={theme === THEME_LIGHT}
                    onChange={() => changeTheme(THEME_LIGHT)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="theme-dark"
                    name="theme"
                    label={
                      <div className="d-flex align-items-center">
                        <i className="bi bi-moon-fill me-2"></i>
                        <span>Dark Mode</span>
                      </div>
                    }
                    checked={theme === THEME_DARK}
                    onChange={() => changeTheme(THEME_DARK)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    id="theme-auto"
                    name="theme"
                    label={
                      <div className="d-flex align-items-center">
                        <i className="bi bi-circle-half me-2"></i>
                        <span>Auto (Light 7AM-7PM, Dark 7PM-7AM)</span>
                      </div>
                    }
                    checked={theme === THEME_AUTO}
                    onChange={() => changeTheme(THEME_AUTO)}
                  />
                </div>
                <div className="mt-2 small text-muted">
                  <p className="mb-1">Choose between light, dark, or auto modes for the application interface.</p>
                  <p className="mb-0">Auto mode will switch automatically based on time of day. The divider in the icon rotates like a clock hand to show the current time.</p>
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check 
                  type="switch"
                  id="show-portfolios-nav"
                  label="Show portfolios in navigation panel"
                  checked={settings.show_portfolios_in_nav !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      show_portfolios_in_nav: e.target.checked
                    }));
                  }}
                />
                <Form.Text className="text-muted">
                  When enabled, accounts are grouped by portfolios in the left navigation. When disabled, all accounts are displayed in a flat list.
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button
                  variant={getButtonVariant()}
                  onClick={async () => {
                    if (settings.show_portfolios_in_nav !== undefined) {
                      try {
                        await updateSettings({
                          show_portfolios_in_nav: settings.show_portfolios_in_nav
                        });
                        setOriginalSettings(prev => ({
                          ...prev,
                          show_portfolios_in_nav: settings.show_portfolios_in_nav
                        }));
                        setSaved(true);
                        setTimeout(() => setSaved(false), 2000);

                        // Dispatch event to notify NavigationPanel of setting change
                        const settingChangeEvent = new CustomEvent('navigationSettingChanged', {
                          detail: { show_portfolios_in_nav: settings.show_portfolios_in_nav }
                        });
                        window.dispatchEvent(settingChangeEvent);
                        logger.debug('Settings: Dispatched navigation setting change event:', settings.show_portfolios_in_nav);
                      } catch (err) {
                        setError('Failed to save display settings');
                      }
                    }
                  }}
                  disabled={loading || saving || !hasSectionChanges(['show_portfolios_in_nav'])}
                  className="position-relative"
                  style={{ transition: 'all 0.3s ease' }}
                >
                  {saved ? 'Saved!' : 'Save Display Settings'}
                  {saved && (
                    <i className="bi bi-check-circle-fill position-absolute"
                       style={{
                         right: '10px',
                         top: '50%',
                         transform: 'translateY(-50%)'
                       }}
                    ></i>
                  )}
                </Button>
              </div>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>

        {/* Right Column */}
        <Col md={6}>
          {/* Investment Settings */}
          <Card className="mb-4">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setCardsExpanded(prev => ({ ...prev, investment: !prev.investment }))}
            >
              <span>
                <i className="bi bi-graph-up me-2"></i>
                Investment Settings
              </span>
              <i className={`bi bi-chevron-${cardsExpanded.investment ? 'down' : 'right'}`}></i>
            </Card.Header>
            <Collapse in={cardsExpanded.investment}>
              <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Maximum Tickers Per Account</Form.Label>
                <Form.Control
                  type="number"
                  name="max_tickers_per_account"
                  value={settings.max_tickers_per_account || 10}
                  onChange={handleInputChange}
                  min="1"
                  max="100"
                />
                <Form.Text className="text-muted">
                  Maximum number of different tickers allowed per account. This limit is enforced when creating BUY orders.
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button
                  variant={getButtonVariant()}
                  onClick={async () => {
                    if (settings.max_tickers_per_account !== undefined) {
                      try {
                        await updateSettings({
                          max_tickers_per_account: settings.max_tickers_per_account
                        });
                        setOriginalSettings(prev => ({
                          ...prev,
                          max_tickers_per_account: settings.max_tickers_per_account
                        }));
                        setSaved(true);
                        setTimeout(() => setSaved(false), 2000);
                      } catch (err) {
                        setError('Failed to save investment settings');
                      }
                    }
                  }}
                  disabled={loading || saving || !hasSectionChanges(['max_tickers_per_account'])}
                  className="position-relative"
                  style={{ transition: 'all 0.3s ease' }}
                >
                  {saved ? 'Saved!' : 'Save Investment Settings'}
                  {saved && (
                    <i className="bi bi-check-circle-fill position-absolute"
                       style={{
                         right: '10px',
                         top: '50%',
                         transform: 'translateY(-50%)'
                       }}
                    ></i>
                  )}
                </Button>
              </div>
              </Card.Body>
            </Collapse>
          </Card>

          {/* Weather Settings */}
          <Card className="mb-4">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setCardsExpanded(prev => ({ ...prev, weather: !prev.weather }))}
            >
              <span>
                <i className="bi bi-cloud-sun-fill me-2"></i>
                Weather Settings
              </span>
              <i className={`bi bi-chevron-${cardsExpanded.weather ? 'down' : 'right'}`}></i>
            </Card.Header>
            <Collapse in={cardsExpanded.weather}>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Default City</Form.Label>
                  <Form.Control
                    type="text"
                    value={localStorage.getItem('weatherCity') || 'New York'}
                    onChange={(e) => localStorage.setItem('weatherCity', e.target.value)}
                  />
                  <Form.Text className="text-muted">City for automatic weather-based theming</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Units</Form.Label>
                  <Form.Select
                    value={localStorage.getItem('weatherUnit') || 'imperial'}
                    onChange={(e) => localStorage.setItem('weatherUnit', e.target.value)}
                  >
                    <option value="imperial">Imperial (°F)</option>
                    <option value="metric">Metric (°C)</option>
                  </Form.Select>
                </Form.Group>
                <div className="d-flex justify-content-end">
                  <Button
                    onClick={() => {
                      // Update original values to current values
                      setOriginalWeatherCity(localStorage.getItem('weatherCity') || 'New York');
                      setOriginalWeatherUnit(localStorage.getItem('weatherUnit') || 'imperial');
                      // simple visual saved hint
                      setSaved(true);
                      setTimeout(() => setSaved(false), 1500);
                    }}
                    variant={getButtonVariant()}
                    disabled={loading || saving || !hasWeatherSettingsChanged()}
                  >
                    {saved ? 'Saved!' : 'Save Weather Settings'}
                  </Button>
                </div>
              </Card.Body>
            </Collapse>
          </Card>

          {/* Weather Theme */}
          <Card className="mb-4">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setCardsExpanded(prev => ({ ...prev, weatherTheme: !prev.weatherTheme }))}
            >
              <span>
                <i className="bi bi-palette-fill me-2"></i>
                Weather Theme
              </span>
              <i className={`bi bi-chevron-${cardsExpanded.weatherTheme ? 'down' : 'right'}`}></i>
            </Card.Header>
            <Collapse in={cardsExpanded.weatherTheme}>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="switch"
                    id="weather-theme-enabled"
                    label="Enable weather-based theming"
                    checked={!!weatherThemeEnabled}
                    onChange={toggleWeatherTheme}
                  />
                  <Form.Text className="text-muted">When enabled, UI colors adapt to weather or seasons</Form.Text>
                </Form.Group>

                <fieldset disabled={!weatherThemeEnabled}>
                  <Form.Group className="mb-3">
                    <Form.Label>Theme Mode</Form.Label>
                    <div className="d-flex flex-column">
                      <Form.Check type="radio" id="wt-auto" name="wt-mode" label="Automatic (real weather)" checked={weatherThemeMode === 'auto'} onChange={() => updateWeatherThemeMode('auto')} className="mb-2" />
                      <Form.Check type="radio" id="wt-manual" name="wt-mode" label="Manual selection" checked={weatherThemeMode === 'manual'} onChange={() => updateWeatherThemeMode('manual')} className="mb-2" />
                      <Form.Check type="radio" id="wt-cycle-weather" name="wt-mode" label="Daily Cycle – Weather" checked={weatherThemeMode === 'cycle_weather'} onChange={() => updateWeatherThemeMode('cycle_weather')} className="mb-2" />
                      <Form.Check type="radio" id="wt-cycle-seasons" name="wt-mode" label="Daily Cycle – Seasons" checked={weatherThemeMode === 'cycle_seasons'} onChange={() => updateWeatherThemeMode('cycle_seasons')} className="mb-2" />
                      <Form.Check type="radio" id="wt-cycle-all" name="wt-mode" label="Daily Cycle – All" checked={weatherThemeMode === 'cycle_all'} onChange={() => updateWeatherThemeMode('cycle_all')} />
                    </div>
                  </Form.Group>

                  {weatherThemeMode === 'manual' && (
                    <Form.Group className="mb-2">
                      <Form.Label>Select Theme</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {['clear','clouds','rain','snow','thunderstorm','spring','summer','autumn','winter'].map((t) => (
                          <Button key={t} size="sm" variant={selectedWeatherTheme === t ? 'primary' : 'outline-primary'} onClick={() => updateSelectedWeatherTheme(t)}>
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>
                  )}
                </fieldset>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Settings; 